/**
 * FullPipelineButton - Button for running the full entity sync pipeline.
 * Shows status in the button text while polling for updates.
 */

import { CheckCircle2, Loader2, Play, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  ENTITY_SYNC_TYPES,
  useEntitySyncGetPipelineStatusQuery,
  useEntitySyncRunFullPipelineMutation,
} from '@/services/api/entitySyncService';

type OverallStatus = 'RUNNING' | 'COMPLETED' | 'FAILED';

interface PipelineStatus {
  workflow_id?: string;
  overall_status?: OverallStatus;
  current_step?: string | null;
  error_message?: string | null;
  entity_sync_status?: string;
  link_suggestions_status?: string;
  data_suggestions_status?: string;
}

interface FullPipelineButtonProps {
  matterId: string;
  entityType?: string;
  onPipelineComplete?: () => void;
  buttonSize?: React.ComponentProps<typeof Button>['size'];
  className?: string;
}

const STEP_LABELS: Record<string, string> = {
  entity_sync: 'Syncing Entities...',
  link_suggestions: 'Generating Links...',
  data_suggestions: 'Generating Suggestions...',
};

export const FullPipelineButton: React.FC<FullPipelineButtonProps> = ({
  matterId,
  entityType = ENTITY_SYNC_TYPES.MEDICAL_PROVIDERS,
  onPipelineComplete,
  buttonSize = 'default',
  className,
}) => {
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [lastStatus, setLastStatus] = useState<OverallStatus | null>(null);

  // Run pipeline mutation
  const [runFullPipeline, { isLoading: isStarting }] =
    useEntitySyncRunFullPipelineMutation();

  // Poll for status when we have a workflow ID
  const { data: statusData } = useEntitySyncGetPipelineStatusQuery(
    { matterId, entityType, workflowId: workflowId ?? '' },
    {
      skip: !workflowId || !isPolling,
      pollingInterval: isPolling ? 2000 : undefined,
      skipPollingIfUnfocused: true,
    },
  );

  const status = statusData as PipelineStatus | undefined;

  // Handle pipeline completion
  useEffect(() => {
    if (status?.overall_status === 'COMPLETED') {
      setIsPolling(false);
      setLastStatus('COMPLETED');
      onPipelineComplete?.();
      // Reset after 3 seconds
      const timer = setTimeout(() => {
        setLastStatus(null);
        setWorkflowId(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
    if (status?.overall_status === 'FAILED') {
      setIsPolling(false);
      setLastStatus('FAILED');
      // Reset after 5 seconds
      const timer = setTimeout(() => {
        setLastStatus(null);
        setWorkflowId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status?.overall_status, onPipelineComplete]);

  // Start the pipeline
  const handleStartPipeline = useCallback(async () => {
    setLastStatus(null);

    try {
      const result = await runFullPipeline({
        matterId,
        entityType,
        generateSuggestionsRequestSchema: { supersede_existing: true },
      }).unwrap();

      setWorkflowId(result.workflow_id);
      setIsPolling(true);
    } catch {
      setLastStatus('FAILED');
      // Reset after 5 seconds
      setTimeout(() => setLastStatus(null), 5000);
    }
  }, [matterId, entityType, runFullPipeline]);

  const isRunning = isStarting || isPolling;

  // Get button content based on state
  const getButtonContent = () => {
    if (isStarting) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Starting...
        </>
      );
    }

    if (isPolling && status?.current_step) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {STEP_LABELS[status.current_step] ?? 'Running...'}
        </>
      );
    }

    if (isPolling) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Running...
        </>
      );
    }

    if (lastStatus === 'COMPLETED') {
      return (
        <>
          <CheckCircle2 className="h-4 w-4" />
          Pipeline Complete
        </>
      );
    }

    if (lastStatus === 'FAILED') {
      return (
        <>
          <XCircle className="h-4 w-4" />
          Pipeline Failed
        </>
      );
    }

    return (
      <>
        <Play className="h-4 w-4" />
        Sync Now
      </>
    );
  };

  const getButtonVariant = () => {
    if (lastStatus === 'COMPLETED') return 'outline';
    if (lastStatus === 'FAILED') return 'destructive';
    return 'default';
  };

  return (
    <Button
      onClick={handleStartPipeline}
      disabled={isRunning}
      size={buttonSize}
      className={className ? `gap-2 ${className}` : 'gap-2'}
      variant={getButtonVariant()}
    >
      {getButtonContent()}
    </Button>
  );
};

export default FullPipelineButton;
